import { useEffect, useState } from "react";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Container, Typography, Card, CardContent } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { firestore } from "../firebase";

const CACHE_KEY_AGENT = "cachedAgentData";
const CACHE_KEY_SHOPS = "cachedShopsData";
const CACHE_KEY_EARNINGS = "cachedEarningsData";

const AffiliateDashboard = () => {
  const auth = getAuth();
  const db = getFirestore();

  // Load cached data instantly
  const [agentData, setAgentData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY_AGENT)) || null;
    } catch {
      return null;
    }
  });

  const [earnings, setEarnings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY_EARNINGS)) || [];
    } catch {
      return [];
    }
  });

  const [shops, setShops] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY_SHOPS)) || [];
    } catch {
      return [];
    }
  });

  // Set loading to false immediately if cached data exists
  const [loading, setLoading] = useState(!agentData);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchAffiliateData(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchAffiliateData = async (userId) => {
    try {
      const agentRef = doc(db, "affiliates", userId);
      const agentSnap = await getDoc(agentRef);

      if (agentSnap.exists()) {
        const agentInfo = agentSnap.data();
        setAgentData(agentInfo);
        localStorage.setItem(CACHE_KEY_AGENT, JSON.stringify(agentInfo));

        await fetchEarnings(userId);
        await fetchShops(agentInfo.assignedShops);
      }
    } catch (error) {
      console.error("Error fetching agent data:", error);
    } finally {
      setLoading(false); // Set loading to false after Firestore fetch
    }
  };

  const fetchEarnings = async (agentId) => {
    try {
      const earningsRef = collection(db, "affiliates", agentId, "earnings");
      const earningsSnap = await getDocs(earningsRef);
      const earningsData = earningsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setEarnings(earningsData);
      localStorage.setItem(CACHE_KEY_EARNINGS, JSON.stringify(earningsData));
    } catch (error) {
      console.error("Error fetching earnings:", error);
    }
  };

  const fetchShops = async (shopIds) => {
    try {
      const shopsData = await Promise.all(
        shopIds.map(async (shopId) => {
          const shopRef = doc(db, "rentalShops", shopId);
          const shopSnap = await getDoc(shopRef);
          return shopSnap.exists() ? { id: shopId, ...shopSnap.data() } : null;
        })
      );

      const filteredShops = shopsData.filter((shop) => shop !== null);
      setShops(filteredShops);
      localStorage.setItem(CACHE_KEY_SHOPS, JSON.stringify(filteredShops));
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (!agentData) return <Typography>No affiliate data found.</Typography>;

  return (
    <Container>
      <Card>
        <CardContent>
          <Typography variant="h6">Overview</Typography>
          <Typography>
            <strong>Assigned Shops:</strong> {shops.length}
          </Typography>
          <Typography>
            <strong>Total Earnings:</strong> $
            {earnings.reduce((sum, e) => sum + e.commissionEarned, 0)}
          </Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6">Earnings Breakdown</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={earnings}>
              <XAxis dataKey="shopId" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="commissionEarned" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6">Assigned Shops</Typography>
          <ul>
            {shops.map((shop) => (
              <li key={shop.id}>
                <strong>{shop.name}</strong>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AffiliateDashboard;
